import './Text.scss';
import React, { FC, Suspense, lazy } from 'react';
import { parseText } from './parseText';
import { Text as TextFields, useGetTextEditQuery } from '@/generated/graphql';
import { useUserCurrent } from '@/components/provider/UserCurrentProvider';
import Loading from '@/components/basic/Loading/Loading';

const TextEdit = lazy(
    () => import(/* webpackChunkName: "edit-main" */ './TextEdit'),
);
const EditShell = lazy(
    () =>
        import(
            /* webpackChunkName: "edit-main" */ '@/components/edit/EditShell/EditShell'
        ),
);

type TextProps = {
    fields: TextFields;
    panelOwnerId: number;
};

const Text: FC<TextProps> = ({ fields, panelOwnerId }) => {
    const { userCurrent } = useUserCurrent();

    const { data, loading, error } = useGetTextEditQuery({
        variables: {
            textId: fields.id,
        },
        skip: userCurrent?.isEditMode !== 'MEDIA',
        fetchPolicy: 'network-only',
    });

    if (loading) {
        return <Loading type="element" />;
    }

    if (error) {
        return <pre>Error...{JSON.stringify(error, null, 2)}</pre>;
    }

    const textNonEditContent = (
        <>
            <section className="Tx" suppressHydrationWarning={true}>
                {fields.text && parseText(fields.text)}
            </section>
            <div className="Tx_clear"></div>
        </>
    );

    if (userCurrent?.isEditMode === 'MEDIA' && !loading && data!.textEdit) {
        const passEditState = {
            editKey: fields.id,
            componentType: 'TEXT',
            fields: {
                adminTitle: data.textEdit.adminTitle,
                adminNote: data.textEdit.adminNote,
            },
            localFields: {
                text: fields.text || '',
                textJson: data.textEdit.textJson,
            },
            ownerId: panelOwnerId,
        };
        return (
            <Suspense fallback={textNonEditContent}>
                <EditShell passEditState={passEditState}>
                    <TextEdit fields={fields} />
                </EditShell>
            </Suspense>
        );
    }

    return textNonEditContent;
};

export default Text;
